<script lang="ts" setup>
import VNuxtLink from '~/components/v-nuxt-link/VNuxtLink.vue'
import type { ApiSearchAutocomplete } from '~~/server/api/search/autocomplete'

export interface SearchAutocompleteResults {
    data: ApiSearchAutocomplete
}

const props = defineProps<SearchAutocompleteResults>()
const currentIndex = ref<number>(-1)
const linksRefs = ref<Array<InstanceType<typeof VNuxtLink>> | null>(null)

const setFocusedLink = (): void => {
    if (!linksRefs?.value?.[currentIndex.value]) {
        return
    }

    linksRefs.value?.[currentIndex.value]?.$el?.focus()
}
const handleClickArrowUp = (e: KeyboardEvent): void => {
    if (!linksRefs?.value) {
        return
    }

    e.preventDefault()
    e.stopImmediatePropagation()

    if (currentIndex.value === 0) {
        currentIndex.value = linksRefs?.value?.length - 1
        return
    }

    currentIndex.value -= 1
    setFocusedLink()
}

const handleClickArrowDown = (e: KeyboardEvent): void => {
    if (!linksRefs?.value) {
        return
    }

    e.preventDefault()
    e.stopImmediatePropagation()

    if (currentIndex.value === linksRefs.value?.length - 1) {
        currentIndex.value = 0
        return
    }

    currentIndex.value += 1
    setFocusedLink()
}

const handleClickOnArrows = (e: KeyboardEvent): void => {
    const { key } = e
    if (key === 'ArrowUp') handleClickArrowUp(e)
    if (key === 'ArrowDown') handleClickArrowDown(e)
}

onMounted(() => {
    document.addEventListener('keydown', handleClickOnArrows)
})
onUnmounted(() => {
    document.removeEventListener('keydown', handleClickOnArrows)
})
</script>
<template>
  <ul class="overflow-auto">
    <li
      v-for="(category, index) in props.data"
      :key="`sra-${index}`"
    >
      <Label
        :id="`sra-${index}`"
        :label="category.category"
        background-color="var(--global-color-black-100)"
        text-color="var(--global-color-white)"
        class="d-inline-flex"
        :class="{ 'mt-2': index > 0 }"
      />
      <ul
        :aria-labelledby="`sra-${index}`"
        role="group"
      >
        <li
          v-for="(item, index2) in category.data"
          :key="`srci-${index2}`"
          role="option"
          class="mt-2"
        >
          <Tracker :data="{ title: item.title }">
            <VNuxtLink
              ref="linksRefs"
              tabindex="0"
              :to="useNamedRoutesLink(item)"
              :label="item.url"
              class="fs-small text-black d-block transition hover-text-decoration-underline"
            >
              {{ item.title }}
            </VNuxtLink>
          </Tracker>
        </li>
      </ul>
    </li>
  </ul>
  <SearchResultsTotal v-if="props.data.length" />
</template>
